import React from "react";
import cx from "classnames";
import JSZip from "jszip";
import { useTransition, animated } from "@react-spring/web";
import { toast } from "react-toastify";
import Dropzone, { DropzoneRef } from "react-dropzone";

import Slider from "src/components/slider";
import Button from "src/components/button";
import Toast from "src/components/toast";
import { Input } from "src/components/input";
import {
  PPT_IMPORT_CONTENT_TYPES,
  getAllowedContentTypes,
} from "src/pages/experiences/constants/storyboard";
import { StoryboardType } from "src/types/experiences/storyboardSettings/storyboard";
import { useStoryboardImport } from "src/pages/experiences/storyboards/hooks/useStoryboardImport";
import { useWorkspaceSubscription } from "src/pages/experiences/hooks/useWorkspaceSubscription";

import { ContentTypeSelector } from "./ContentTypeSelector";

import styles from "./importDesignDialog.module.scss";

type Phase = "idle" | "configuring";

interface ImportDesignDialogProps {
  show: boolean;
  onClose: () => void;
  workspaceId: string;
}

const sanitizeName = (rawName: string) =>
  rawName.replace(/\u202F/g, "").replace(/\.pptx$/i, "").trim();

const extractPptxThumbnail = async (file: File): Promise<string | null> => {
  try {
    const zip = await JSZip.loadAsync(file);

  const imageEntry =
    zip.file(/^ppt\/media\/.*\.(png|jpg|jpeg)$/i)?.[1] ?? null;

  if (!imageEntry) return null;

  const blob = await imageEntry.async("blob");
  return URL.createObjectURL(blob);

  } catch (err) {
    console.error(err);
    return null;
  }
};
export const ImportDesignDialog: React.FC<ImportDesignDialogProps> = ({
  show,
  onClose,
  workspaceId,
}) => {
  const [phase, setPhase] = React.useState<Phase>("idle");
  const [file, setFile] = React.useState<File | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = React.useState<string | null>(null);
  const [name, setName] = React.useState("");
  const [contentType, setContentType] = React.useState<StoryboardType | null>(
    null,
  );
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const dropZoneRef = React.useRef<DropzoneRef>(null);
  const [dragging, setDragging] = React.useState(false);
  const thumbnailUrlRef = React.useRef<string | null>(null);
  const configureRef = React.useRef<HTMLDivElement>(null);
  const [atBottom, setAtBottom] = React.useState(false);

  const { startImport } = useStoryboardImport();
  const subscription = useWorkspaceSubscription();

  const allowedTypes = React.useMemo(
    () => getAllowedContentTypes(subscription, PPT_IMPORT_CONTENT_TYPES),
    [subscription],
  );

  const setThumbnail = React.useCallback((url: string | null) => {
    if (thumbnailUrlRef.current) {
      URL.revokeObjectURL(thumbnailUrlRef.current);
    }
    thumbnailUrlRef.current = url;
    setThumbnailUrl(url);
  }, []);

  React.useEffect(() => {
    return () => {
      if (thumbnailUrlRef.current) {
        URL.revokeObjectURL(thumbnailUrlRef.current);
        thumbnailUrlRef.current = null;
      }
    };
  }, []);

  const resetState = React.useCallback(() => {
    setPhase("idle");
    setFile(null);
    setThumbnail(null);
    setName("");
    setContentType(null);
    setDragging(false);
  }, [setThumbnail]);

  React.useEffect(() => {
    if (!show) resetState();
  }, [show, resetState]);

  React.useEffect(() => {
    if (phase !== "configuring") return;

    const scroller = configureRef.current?.closest<HTMLElement>(
      ".offcanvas-body",
    );
    if (!scroller) return;

    const update = () => {
      const remaining =
        scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
      setAtBottom(remaining <= 1);
    };

    update();
    scroller.addEventListener("scroll", update, { passive: true });

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(scroller);
    if (configureRef.current) resizeObserver.observe(configureRef.current);

    return () => {
      scroller.removeEventListener("scroll", update);
      resizeObserver.disconnect();
    };
  }, [phase, contentType, thumbnailUrl]);

  const handleClose = () => {
    onClose();
  };

  const handleFilePicked = (picked: File) => {
    if (!picked.name.toLowerCase().endsWith(".pptx")) {
      toast(
        <Toast.Failure
          heading="Unsupported file"
          message="Please select a .pptx file."
        />,
        { autoClose: 3000 },
      );
      return;
    }
    setFile(picked);
    setThumbnail(null);
    setName(sanitizeName(picked.name) || "Untitled");
    setContentType(null);
    setPhase("configuring");

    extractPptxThumbnail(picked).then((url) => {
      if (!url) return;
      setThumbnail(url);
    });
  };

  const onDrop = React.useCallback((acceptedFiles: File[]) => {
    setDragging(false);
    const picked = acceptedFiles[0];
    if (picked) handleFilePicked(picked);
  }, []);

  const handleDropEnter = () => setDragging(true);
  const handleDropLeave = () => setDragging(false);
  const handleDropOver = () => setDragging(true);

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    if (picked) handleFilePicked(picked);
    e.target.value = "";
  };

  const handleCreate = () => {
    if (!file || !contentType) return;

    const trimmedName = name.trim() || "Untitled";

    // Hand the preview blob URL off to the listing card. Clear our ref (without
    // revoking) so the dialog's unmount cleanup doesn't tear it down — the
    // listing now owns it and revokes it in removeUpload.
    const handoffThumbnail = thumbnailUrlRef.current;
    thumbnailUrlRef.current = null;

    const started = startImport({
      file,
      name: trimmedName,
      contentType,
      thumbnailUrl: handoffThumbnail,
      workspaceId,
    });

    if (started) onClose();
  };

  const canCreate = !!file && !!contentType && !!name.trim();

  const isConfiguring = phase !== "idle";
  const headerTitle = isConfiguring ? "now, choose an experience" : "import design";
  const headerMessage = isConfiguring
    ? "Select an experience to create a storyboard effortlessly with your PPT file."
    : "Import a PPT to bootstrap a new storyboard.";

  const phaseKey = isConfiguring ? "configuring" : "idle";
  const transitions = useTransition(phaseKey, {
    from: { opacity: 0, transform: "translateY(16px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    leave: { opacity: 0, transform: "translateY(-16px)" },
    config: { duration: 300, tension: 30, friction: 240 },
  });

  return (
    <Slider.Dialog
      className={styles.importDesignDialog}
      width="380px"
      show={show}
      onClose={handleClose}
    >
      <Slider.Header
        className={styles.header}
        title={headerTitle}
        message={headerMessage}
      />
      <Slider.Body className={styles.body}>
        <Dropzone
          ref={dropZoneRef}
          noClick={true}
          accept={{
            "application/vnd.openxmlformats-officedocument.presentationml.presentation":
              [".pptx"],
          }}
          onDrop={onDrop}
          onDragEnter={handleDropEnter}
          onDragLeave={handleDropLeave}
          onDragOver={handleDropOver}
          onDropRejected={() =>
            toast(
              <Toast.Failure
                heading="Unsupported file"
                message="Please select a .pptx file."
              />,
              { autoClose: 3000 },
            )
          }
        >
          {({ getRootProps, getInputProps }) => (
            <div className={styles.dropZoneContainer} {...getRootProps()}>
              <input {...getInputProps()} />

              {transitions((spring, key) =>
                key === "idle" ? (
                  <animated.div style={spring} className={styles.phasePane}>
                    <div className={styles.options}>
                      <div
                        className={cx(styles.option, { [styles.selected]: !!file })}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <img
                          src="/images/assets/add/add_from_file.svg"
                          alt="Drag & drop ppt"
                        />
                        <div className={styles.details}>
                          <h6 className={styles.title}>Drag &amp; drop ppt</h6>
                          <span className={styles.description}>
                            Files from anywhere or browse
                          </span>
                          <div className={styles.actions}>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept=".pptx"
                              style={{ display: "none" }}
                              onChange={onFileInputChange}
                            />
                            <Button
                              variant="tertiary"
                              className={styles.action}
                              icon="paperflite-add"
                              text="Browse"
                              onClick={(e) => {
                                e.stopPropagation();
                                fileInputRef.current?.click();
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </animated.div>
                ) : (
                  <animated.div style={spring} className={styles.phasePane}>
                    <div className={styles.configure} ref={configureRef}>
                      <div className={styles.fileRow}>
                        <div className={styles.thumb}>
                          <img
                            src={thumbnailUrl ?? "/images/assets/default_asset.jpg"}
                            alt=""
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).src =
                                "/images/assets/default_asset.jpg";
                            }}
                            className={thumbnailUrl ? styles.thumbPreview : styles.thumbDefault}
                          />
                        </div>
                        <span className={styles.fileName}>{file?.name}</span>
                        <button
                          type="button"
                          className={styles.removeButton}
                          onClick={resetState}
                          aria-label="Remove file"
                        >
                          <i className="fa-regular fa-circle-xmark" />
                        </button>
                      </div>

                      <Input
                        className={styles.nameInput}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Storyboard name"
                      />

                      <ContentTypeSelector
                        selected={contentType}
                        onSelect={(value) => setContentType(value)}
                        allowedTypes={allowedTypes}
                      />
                    </div>
                    <div
                      className={cx(styles.bottomFade, {
                        [styles.bottomFadeHidden]: atBottom,
                      })}
                    />
                  </animated.div>
                )
              )}

              {phase === "idle" && dragging && (
                <div className={styles.dragDrop}>
                  <div className={styles.dragDropContent}>
                    <img src="/images/assets/add/drag_drop.svg" alt="" />
                    <p className={styles.message}>Drop files here to upload</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </Dropzone>
      </Slider.Body>
      <Slider.Actions>
        <Button
          icon="paperflite-tick"
          text="Create"
          variant="primary"
          disabled={!canCreate}
          onClick={handleCreate}
        />
        <Button
          text="Cancel"
          variant="secondary"
          onClick={handleClose}
        />
      </Slider.Actions>
    </Slider.Dialog>
  );
};
